import { c as _c } from "react/compiler-runtime";
import { parseTeamMemberFilterId } from "@components/teams/teams-filters/teams-filter-options";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { useEffect } from "react";
import { teamIdsByMemberSelector } from "@store/teams/teams-selector";
import { fetchTeamIdsOfMember } from "@store/teams/teams-slice-thunk";

/** Separates the identities in the key of the members to fetch, since an identity never contains a line break */
const IDENTITY_SEPARATOR = "\n";
/**
 * Returns the fetching status of the teams of the members the filter applies, and a function to
 * fetch the ones that are missing. The teams of each member are fetched only once, see the teams
 * slice.
 */
export function useTeamsOfFilterMembers(memberFilterIds) {
  const $ = _c(17);
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const teamIdsByMember = useAppSelector(teamIdsByMemberSelector);
  let identities;
  let t0;
  let t1;
  let t2;
  if ($[0] !== memberFilterIds || $[1] !== teamIdsByMember) {
    identities = memberFilterIds.flatMap(_temp);
    let t3;
    if ($[6] !== teamIdsByMember) {
      t3 = identity => teamIdsByMember[identity]?.status ?? "uninitialized";
      $[6] = teamIdsByMember;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    const statuses = identities.map(t3);
    t0 = identities;
    t1 = statuses.some(_temp2);
    t2 = statuses.includes("rejected");
    $[0] = memberFilterIds;
    $[1] = teamIdsByMember;
    $[2] = identities;
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
  } else {
    identities = $[2];
    t0 = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  let t3;
  if ($[8] !== companyId || $[9] !== dispatch || $[10] !== identities) {
    t3 = () => {
      identities.forEach(memberIdentity => {
        dispatch(fetchTeamIdsOfMember({
          companyId,
          memberIdentity
        }));
      });
    };
    $[8] = companyId;
    $[9] = dispatch;
    $[10] = identities;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t0 || $[13] !== t1 || $[14] !== t2 || $[15] !== t3) {
    t4 = {
      identities: t0,
      isLoading: t1,
      hasFailed: t2,
      fetchTeamsOfMembers: t3
    };
    $[12] = t0;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
}

/**
 * Fetches the teams of the members the filter applies, the first time they are applied, and
 * returns whether they are still being fetched. Teams that failed to be fetched are only fetched
 * again when the user asks to, from the members chip, so that a failing request is not repeated.
 */
function _temp2(status) {
  return status === "pending" || status === "uninitialized";
}
function _temp(filterId) {
  return parseTeamMemberFilterId(filterId) ?? [];
}
export function useTeamsOfFilterMembersFetched(memberFilterIds) {
  const $ = _c(10);
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const teamsOfFilterMembers = useTeamsOfFilterMembers(memberFilterIds);
  const teamIdsByMember = useAppSelector(teamIdsByMemberSelector);
  let t0;
  if ($[0] !== teamIdsByMember || $[1] !== teamsOfFilterMembers.identities) {
    let t1;
    if ($[3] !== teamIdsByMember) {
      t1 = identity => teamIdsByMember[identity] === undefined;
      $[3] = teamIdsByMember;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = teamsOfFilterMembers.identities.filter(t1).join(IDENTITY_SEPARATOR);
    $[0] = teamIdsByMember;
    $[1] = teamsOfFilterMembers.identities;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const unfetchedIdentitiesKey = t0;
  let t1;
  let t2;
  if ($[5] !== companyId || $[6] !== dispatch || $[7] !== unfetchedIdentitiesKey) {
    t1 = () => {
      if (!unfetchedIdentitiesKey) {
        return;
      }
      unfetchedIdentitiesKey.split(IDENTITY_SEPARATOR).forEach(memberIdentity => {
        dispatch(fetchTeamIdsOfMember({
          companyId,
          memberIdentity
        }));
      });
    };
    t2 = [companyId, dispatch, unfetchedIdentitiesKey];
    $[5] = companyId;
    $[6] = dispatch;
    $[7] = unfetchedIdentitiesKey;
    $[8] = t1;
    $[9] = t2;
  } else {
    t1 = $[8];
    t2 = $[9];
  }
  useEffect(t1, t2);
  return teamsOfFilterMembers;
}